import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, BellOff, BellRing, X, Clock, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: notification.id || Date.now().toString(),
      title: notification.title,
      body: notification.message || notification.body,
      timestamp: notification.timestamp || new Date().toISOString(),
      read: false,
      type: notification.type,
      data: notification.data
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev].slice(0, 50); // Keep last 50
      localStorage.setItem('ikonic_notifications', JSON.stringify(updated));
      return updated;
    });
    
    setUnreadCount(prev => prev + 1);

    // Show browser notification if enabled
    if (notificationsEnabled && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message || notification.body,
        icon: 'https://customer-assets.emergentagent.com/job_propmatch-7/artifacts/y6a3qiy4_LogotipoFinal_IkonicDeals%28web%29.jpg'
      });
    }

    // Show toast
    toast.info(notification.title, {
      description: notification.message || notification.body,
      duration: 5000
    });
  }, [notificationsEnabled]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    // Get WebSocket URL (convert https to wss)
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/api/ws/notifications/${token}`);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_lead') {
            addNotification(data);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code);
        setWsConnected(false);
        
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send('ping');
        }
      }, 30000);

      return () => clearInterval(pingInterval);
    } catch (e) {
      console.error('Failed to connect WebSocket:', e);
    }
  }, [addNotification]);

  useEffect(() => {
    // Check if notifications are supported
    setIsSupported('Notification' in window);
    setNotificationsEnabled(Notification.permission === 'granted');
    
    // Load saved notifications
    const saved = localStorage.getItem('ikonic_notifications');
    if (saved) {
      const parsed = JSON.parse(saved);
      setNotifications(parsed);
      setUnreadCount(parsed.filter(n => !n.read).length);
    }

    // Connect to WebSocket
    connectWebSocket();

    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  const requestPermission = async () => {
    if (!isSupported) return false;
    const permission = await Notification.requestPermission();
    const granted = permission === 'granted';
    setNotificationsEnabled(granted);
    if (granted) {
      toast.success("Notificaciones activadas");
    } else {
      toast.error("Permisos de notificación denegados");
    }
    return granted;
  };

  const markAsRead = (id) => {
    const updated = notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    );
    setNotifications(updated);
    setUnreadCount(updated.filter(n => !n.read).length);
    localStorage.setItem('ikonic_notifications', JSON.stringify(updated));
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    setUnreadCount(0);
    localStorage.setItem('ikonic_notifications', JSON.stringify(updated));
  };

  const clearNotification = (id) => {
    const updated = notifications.filter(n => n.id !== id);
    setNotifications(updated);
    setUnreadCount(updated.filter(n => !n.read).length);
    localStorage.setItem('ikonic_notifications', JSON.stringify(updated));
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h';
    return date.toLocaleDateString('es-GT');
  };

  if (!isSupported) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-[#A3A3A3] hover:text-white"
          data-testid="notification-bell"
        >
          {notificationsEnabled ? (
            unreadCount > 0 ? <BellRing size={20} className="text-[#D4AF37]" /> : <Bell size={20} />
          ) : (
            <BellOff size={20} />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-[#121212] border-white/10"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="text-white flex items-center gap-2">
            Notificaciones
            {wsConnected ? (
              <Wifi size={12} className="text-green-500" title="Conectado" />
            ) : (
              <WifiOff size={12} className="text-red-500" title="Desconectado" />
            )}
          </span>
          {notifications.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-[#D4AF37]"
              onClick={markAllAsRead}
            >
              Marcar leídas
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        
        {!notificationsEnabled ? (
          <div className="p-4 text-center">
            <BellOff size={32} className="mx-auto mb-2 text-[#A3A3A3]" />
            <p className="text-[#A3A3A3] text-sm mb-3">
              Activa las notificaciones para recibir alertas
            </p>
            <Button 
              size="sm" 
              onClick={requestPermission}
              className="bg-[#D4AF37] text-black hover:bg-[#FFE082]"
            >
              Activar
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-[#A3A3A3]">
            <Bell size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay notificaciones</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.slice(0, 10).map((notification) => (
              <DropdownMenuItem 
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  !notification.read ? 'bg-[#D4AF37]/5' : ''
                }`}
                onClick={() => markAsRead(notification.id)}
              >
                <span className="text-xl">🔔</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.read ? 'text-[#A3A3A3]' : 'text-white font-medium'}`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-[#666] truncate">{notification.body}</p>
                  <p className="text-xs text-[#666] mt-1 flex items-center gap-1">
                    <Clock size={10} />
                    {formatTime(notification.timestamp)}
                  </p>
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    clearNotification(notification.id);
                  }}
                  className="text-[#666] hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
